import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { Formik } from 'formik';

import { buildSetAuthUser } from '../../../lib/services/auth.service';
import { useRequestParentContactCode, useVerifyParentContact } from '../../../lib/services/parent.service';
import { SessionType } from '../../../lib/types/session.types';
import { IParentVerifyResult } from '../../../lib/types';
import { parentPostLoginPath } from '../../../lib/helpers/parent-links.helpers';
import { TwoStepOtpForm } from '../../_core/TwoStepOtpForm/two-step-otp-form.component';
import { useTwoStepOtp } from '../../_core/TwoStepOtpForm/use-two-step-otp';
import {
  otpRequestValidationSchema,
  otpVerifyValidationSchema,
} from '../../_core/TwoStepOtpForm/two-step-otp-form.validation';

// Maps the verify-response shape onto the IUser shape buildSetAuthUser expects.
// Parents are never admin; first email/phone is the primary.
const toAuthUser = (result: IParentVerifyResult): IUser => ({
  _id: result._id,
  name: result.name,
  email: result.emails[0] || '',
  phone: result.phones[0],
  isAdmin: false,
  token: result.token || undefined,
});

const COPY = {
  requestHeading: 'Parent Sign In',
  requestDescription: 'Enter the email or phone number your association has on file and we\'ll send you a one-time code.',
  verifySubmitLabel: 'Verify & sign in',
};

export const ParentLoginFormContainer: React.FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const onSetAuthUser = buildSetAuthUser();
  const [requestCode] = useRequestParentContactCode();
  const [verifyContact] = useVerifyParentContact();

  const otp = useTwoStepOtp({
    onRequest: async (destination) => {
      await requestCode({ variables: { destination } });
    },
    onVerify: async (destination, code) => {
      const response = await verifyContact({ variables: { destination, code } });
      const result: IParentVerifyResult | undefined = response.data?.parentVerifyContact;
      if (!result?.token) throw new Error('Login response missing session token.');
      await onSetAuthUser(toAuthUser(result), SessionType.PARENT);
      // `?next=` is the route gate's record of where an email deep link was headed.
      history.push(parentPostLoginPath(search));
    },
  });

  // key={otp.step} forces a fresh Formik mount on each step transition so the
  // request-step identifier doesn't leak back when the user clicks "Use a
  // different email or phone" from the verify step.
  if (otp.step === 'verify') {
    return (
      <Formik
        key="verify"
        initialValues={{ code: '' }}
        validationSchema={otpVerifyValidationSchema}
        onSubmit={otp.handleVerify}
      >
        {(form) => (
          <TwoStepOtpForm
            step="verify"
            form={form}
            destination={otp.destination}
            submitError={otp.submitError}
            onBack={otp.onBack}
            copy={COPY}
            resendSecondsRemaining={otp.resendSecondsRemaining}
            resending={otp.resending}
            onResend={otp.handleResend}
          />
        )}
      </Formik>
    );
  }

  return (
    <Formik
      key="request"
      initialValues={{ identifier: '' }}
      validationSchema={otpRequestValidationSchema}
      onSubmit={otp.handleRequest}
    >
      {(form) => (
        <TwoStepOtpForm
          step="request"
          form={form}
          destination={otp.destination}
          submitError={otp.submitError}
          onBack={otp.onBack}
          copy={COPY}
        />
      )}
    </Formik>
  );
};
